import type { Language, Locale, Region } from '@lemonade-hq/lemonation';
import { DEFAULT_LOCALE, DE_REGION, FR_REGION, GB_REGION, NL_REGION, US_REGION } from '@lemonade-hq/lemonation';
import type {
  AnalyticsConfig,
  AuthConfig,
  ContentBundlesPerLocale,
  InfraConfig,
  LayoutConfig,
  LocaleConfig,
  MonitoringConfig,
  NavConfig,
  RealtimeConfig,
  SecurityConfig,
  UserConfig,
} from '@lemonade-hq/nuiza';
import { Env } from '@lemonade-hq/nuiza';
import { getEnvVar } from '@lemonade-hq/ts-helpers';

// WINDOW INTERFACE *******************

declare global {
  interface Window {
    readonly mixpanel?: Record<string, unknown>;
    readonly lemonade?: {
      readonly statusCode: number;
      readonly payload?: {
        readonly scriptName: string;
        readonly quotePublicId: string;
        readonly renewalPolicyPublicId?: string;
        readonly utmCampaign?: string;
        readonly utmSource?: string;
      };
      readonly config?: {
        readonly locale?: {
          readonly locale: Locale;
          readonly region: Region;
          readonly lang: Language;
          readonly translations: ContentBundlesPerLocale;
        };
        readonly urls?: {
          readonly monolithUrl?: string;
          readonly consumerUrl?: string;
        };
        readonly user?: {
          readonly userId: string;
          readonly name: string;
          readonly givenName: string;
          readonly familyName: string;
          readonly email: string;
          readonly dateOfBirth?: string;
        };
        readonly csrf: string;
        readonly realtime?: {
          readonly channels: string[];
          readonly pubnubSubscribeKey: string;
        };
      };
    };
  }
}

const ENV = process.env.STAGE as Env;

// CONFIG *******************
export function googleMapConfig(): string {
  return process.env.GOOGLE_MAP_KEY ?? '';
}

export function analyticsConfig(): AnalyticsConfig {
  return {
    enableGtm: process.env.NEXT_PUBLIC_ENABLE_GTM === 'true',
    segmentKey: process.env.NEXT_PUBLIC_SEGMENT_ID,
    gtmId: undefined, // Change to avoid loading GTM from Monolu
  };
}

export function errorConfig(): {
  readonly hasError: boolean;
  readonly statusCode: number;
} {
  const statusCode = 200;
  const hasError = statusCode >= 400;
  return { hasError, statusCode };
}

export function infraConfig(): InfraConfig {
  return {
    env: (process.env.NEXT_PUBLIC_STAGE ?? Env.Development) as Env,
    envId: process.env.NEXT_PUBLIC_ENV_ID ?? 'master',
    service: 'marketing-edge',
  };
}

export function localeConfig(): LocaleConfig {
  const currentLocale = DEFAULT_LOCALE;
  return {
    lang: 'fr',
    region: 'FR' as Region,
    activeRegions: [DE_REGION, FR_REGION, NL_REGION, US_REGION, GB_REGION],
    includeCommonI18nContext: true,
    translations: {
      [currentLocale]: DEFAULT_LOCALE,
    },
  };
}

export function layoutConfig(hideCookieConsent: boolean): LayoutConfig {
  return {
    header: {
      hide: true,
      fixed: false,
    },
    footer: {
      hide: true,
    },
    aua: {
      hide: true,
      endpoint: '', // TODO: fix this
    },
    cookieConsent: {
      hide: hideCookieConsent,
    },
  };
}

export function monitoringConfig(): MonitoringConfig {
  const sentryDsn = getEnvVar('CLIENT_SENTRY_DSN', '');

  return {
    sentry: {
      dsn: sentryDsn,
      enabled: ENV !== 'development' && sentryDsn != null && sentryDsn.length > 0,
      environment: ENV,
      release: getEnvVar('RELEASE', ''),
    },
  };
}

export function navConfig(): NavConfig {
  return {
    monolithUrl: process.env.NEXT_PUBLIC_MONOLITH_URL ?? 'https://www.lemonade.com',
    consumerUrl: 'https://me.lemonade.com',
  };
}

export function securityConfig(): SecurityConfig {
  return {
    csrf: '',
  };
}

export function realtimeConfig(): RealtimeConfig | undefined {
  return undefined;
}

export function userConfig(): UserConfig | undefined {
  const user = undefined;
  if (user == null) return undefined;

  // return {
  // email: user.email,
  // givenName: user.givenName,
  // familyName: user.familyName,
  // userId: user.userId,
  // dateOfBirth: user.dateOfBirth,
  // };
}

export function authConfig(): AuthConfig | undefined {
  return {
    loginUrl: '/auth/login',
    logoutUrl: '/local-auth/force-logout',
    userInfoUrl: '/auth/me',
    unauthorizedBehavior: 'callback',
  };
}
